import React from 'react'

type Props = {
  readonly bodyScroll: boolean
  readonly children: React.ReactNode
  readonly innerRef?: React.RefObject<HTMLDivElement>
}

function ScrollableList({ bodyScroll, children, innerRef }: Props) {
  const outerRef = React.useRef<HTMLDivElement>(null)
  const defaultInnerRef = React.useRef<HTMLDivElement>(null)
  const [footerHeight, setFooterHeight] = React.useState<number>(() => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--footer-height')
    const parsed = parseFloat(raw)
    return Number.isFinite(parsed) ? parsed : 0
  })
  const EXTRA_SPACE_PX = 24

  const scrollToBottom = () => {
    const outer = outerRef.current
    if (!outer) return
    if (bodyScroll) {
      window.scrollTo({ top: Math.max(0, outer.scrollHeight - EXTRA_SPACE_PX), behavior: 'auto' })
    } else {
      outer.scrollTo({ top: Math.max(0, outer.scrollHeight - EXTRA_SPACE_PX), behavior: 'auto' })
    }
  }

  React.useEffect(() => {
    const currentRef = innerRef?.current || defaultInnerRef.current
    if (!currentRef) return
    const resizeObserver = new ResizeObserver(() => scrollToBottom())
    resizeObserver.observe(currentRef)
    const handleFooterHeightChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ height?: number }>
      const newHeight = customEvent.detail?.height
      if (typeof newHeight === 'number' && Number.isFinite(newHeight)) {
        setFooterHeight(newHeight)
      } else {
        const raw = getComputedStyle(document.documentElement).getPropertyValue('--footer-height')
        const parsed = parseFloat(raw)
        if (Number.isFinite(parsed)) setFooterHeight(parsed)
      }
      scrollToBottom()
    }
    const handleExplicitScrollToBottom = () => scrollToBottom()
    window.addEventListener('pilot-footer-height-change', handleFooterHeightChange as EventListener)
    const footerEl = document.querySelector<HTMLElement>('.pilot-completions-footer')
    let footerResizeObserver: ResizeObserver | undefined
    let promptsMutationObserver: MutationObserver | undefined
    if (footerEl) {
      footerResizeObserver = new ResizeObserver(entries => {
        const entry = entries[0]
        const boxSize = (entry as any).borderBoxSize?.[0]?.blockSize || footerEl.offsetHeight
        if (Number.isFinite(boxSize)) setFooterHeight(boxSize + EXTRA_SPACE_PX)
        requestAnimationFrame(() => scrollToBottom())
      })
      footerResizeObserver.observe(footerEl)
      const promptsEl = footerEl.querySelector('.pilot-completions-footer__prompts')
      if (promptsEl) {
        promptsMutationObserver = new MutationObserver(() => {
          requestAnimationFrame(() => scrollToBottom())
        })
        promptsMutationObserver.observe(promptsEl, {
          childList: true,
          subtree: true,
          attributes: true,
        })
      }
    }
    window.addEventListener('pilot-scroll-to-bottom', handleExplicitScrollToBottom as EventListener)
    return () => {
      window.removeEventListener(
        'pilot-footer-height-change',
        handleFooterHeightChange as EventListener
      )
      window.removeEventListener(
        'pilot-scroll-to-bottom',
        handleExplicitScrollToBottom as EventListener
      )
      if (footerResizeObserver) footerResizeObserver.disconnect()
      if (promptsMutationObserver) promptsMutationObserver.disconnect()
      resizeObserver.disconnect()
    }
  }, [])

  return (
    <div
      className='pilot-completions-list'
      ref={outerRef}
      style={{ paddingBottom: footerHeight + 'px' }}
    >
      <div className='pilot-completions-list__inner_scroll' ref={innerRef || defaultInnerRef}>
        {children}
      </div>
    </div>
  )
}

export default ScrollableList
